import React from "react";
import { Card, CardBody, Container, Row, Col, Table } from "reactstrap";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import { Pie, Bar, Doughnut } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  BarElement,
  ArcElement,
} from "chart.js";
import AnalyticsNavigation from "./components/AnalyticsNavigation";
import config from "../../config";

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

const CategoryAnalytics = () => {
  const [categoriesData, setCategoriesData] = React.useState(null);
  const [subcategoriesData, setSubcategoriesData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);

  React.useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem("authUser");

        // Fetch categories data
        const categoriesResponse = await fetch(
          `${config.API_URL}/api/admin/analytics/visualization/categories`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        // Fetch subcategories data
        const subcategoriesResponse = await fetch(
          `${config.API_URL}/api/admin/analytics/visualization/subcategories`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        if (!categoriesResponse.ok || !subcategoriesResponse.ok) {
          throw new Error("Failed to fetch category analytics data");
        }

        const categoriesDataJson = await categoriesResponse.json();
        const subcategoriesDataJson = await subcategoriesResponse.json();

        if (categoriesDataJson.success && subcategoriesDataJson.success) {
          setCategoriesData(categoriesDataJson.data);
          setSubcategoriesData(subcategoriesDataJson.data);
        } else {
          throw new Error("Failed to fetch category analytics data");
        }
      } catch (err) {
        setError(err.message);
        console.error("Error fetching category analytics data:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Prepare categories event count chart data
  const categoryEventCountChartData = {
    labels: categoriesData
      ? categoriesData.map((category) => category.categoryName)
      : [],
    datasets: [
      {
        label: "Event Count",
        data: categoriesData
          ? categoriesData.map((category) => category.eventCount)
          : [],
        backgroundColor: [
          "rgba(255, 99, 132, 0.6)",
          "rgba(54, 162, 235, 0.6)",
          "rgba(255, 206, 86, 0.6)",
          "rgba(75, 192, 192, 0.6)",
          "rgba(153, 102, 255, 0.6)",
          "rgba(255, 159, 64, 0.6)",
          "rgba(255, 99, 132, 0.6)",
        ],
        borderColor: [
          "rgba(255, 99, 132, 1)",
          "rgba(54, 162, 235, 1)",
          "rgba(255, 206, 86, 1)",
          "rgba(75, 192, 192, 1)",
          "rgba(153, 102, 255, 1)",
          "rgba(255, 159, 64, 1)",
          "rgba(255, 99, 132, 1)",
        ],
        borderWidth: 1,
      },
    ],
  };

  // Prepare categories trading volume chart data
  const categoryVolumeChartData = {
    labels: categoriesData
      ? categoriesData.map((category) => category.categoryName)
      : [],
    datasets: [
      {
        label: "Trading Volume",
        data: categoriesData
          ? categoriesData.map((category) => category.tradingVolume)
          : [],
        backgroundColor: "rgba(54, 162, 235, 0.6)",
        borderColor: "rgba(54, 162, 235, 1)",
        borderWidth: 1,
      },
    ],
  };

  // Prepare top subcategories chart data
  const topSubcategoriesChartData = {
    labels: subcategoriesData
      ? subcategoriesData.slice(0, 6).map((subcat) => subcat.subcategoryName)
      : [],
    datasets: [
      {
        label: "Event Count",
        data: subcategoriesData
          ? subcategoriesData.slice(0, 6).map((subcat) => subcat.eventCount)
          : [],
        backgroundColor: [
          "rgba(255, 99, 132, 0.6)",
          "rgba(54, 162, 235, 0.6)",
          "rgba(255, 206, 86, 0.6)",
          "rgba(75, 192, 192, 0.6)",
          "rgba(153, 102, 255, 0.6)",
          "rgba(255, 159, 64, 0.6)",
        ],
        borderColor: [
          "rgba(255, 99, 132, 1)",
          "rgba(54, 162, 235, 1)",
          "rgba(255, 206, 86, 1)",
          "rgba(75, 192, 192, 1)",
          "rgba(153, 102, 255, 1)",
          "rgba(255, 159, 64, 1)",
        ],
        borderWidth: 1,
      },
    ],
  };

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumbs title="Analytics" breadcrumbItem="Category Analytics" />
        <AnalyticsNavigation activeTab="categories" />

        {loading && (
          <div className="text-center mt-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        )}

        {error && (
          <Card>
            <CardBody>
              <div className="alert alert-danger mb-0">
                Error loading category analytics data: {error}
              </div>
            </CardBody>
          </Card>
        )}

        {categoriesData && subcategoriesData && (
          <>
            <Row>
              <Col xl={6}>
                <Card>
                  <CardBody>
                    <h4 className="card-title mb-4">Events by Category</h4>
                    <div style={{ height: "300px", position: "relative" }}>
                      <Doughnut
                        data={categoryEventCountChartData}
                        options={{
                          maintainAspectRatio: false,
                          responsive: true,
                          plugins: {
                            legend: { position: "right" },
                          },
                        }}
                      />
                    </div>
                  </CardBody>
                </Card>
              </Col>

              <Col xl={6}>
                <Card>
                  <CardBody>
                    <h4 className="card-title mb-4">
                      Trading Volume by Category
                    </h4>
                    <div style={{ height: "300px", position: "relative" }}>
                      <Bar
                        data={categoryVolumeChartData}
                        options={{
                          maintainAspectRatio: false,
                          responsive: true,
                          plugins: {
                            legend: { position: "top" },
                          },
                        }}
                      />
                    </div>
                  </CardBody>
                </Card>
              </Col>
            </Row>

            <Row>
              <Col xl={6}>
                <Card>
                  <CardBody>
                    <h4 className="card-title mb-4">
                      Top Subcategories by Event Count
                    </h4>
                    <div style={{ height: "300px", position: "relative" }}>
                      <Pie
                        data={topSubcategoriesChartData}
                        options={{
                          maintainAspectRatio: false,
                          responsive: true,
                          plugins: {
                            legend: { position: "right" },
                          },
                        }}
                      />
                    </div>
                  </CardBody>
                </Card>
              </Col>

              <Col xl={6}>
                <Card>
                  <CardBody>
                    <h4 className="card-title mb-4">Category Statistics</h4>
                    <div className="table-responsive">
                      <Table className="table-striped mb-0">
                        <thead>
                          <tr>
                            <th>Category</th>
                            <th>Events</th>
                            <th>Active Events</th>
                            <th>Trading Volume</th>
                            <th>User Engagement</th>
                          </tr>
                        </thead>
                        <tbody>
                          {categoriesData.map((category) => (
                            <tr key={category.categoryId}>
                              <td>{category.categoryName}</td>
                              <td>{category.eventCount}</td>
                              <td>{category.activeEvents}</td>
                              <td>${category.tradingVolume.toFixed(2)}</td>
                              <td>{category.userEngagement}</td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  </CardBody>
                </Card>
              </Col>
            </Row>

            <Row>
              <Col xl={12}>
                <Card>
                  <CardBody>
                    <h4 className="card-title mb-4">Subcategory Performance</h4>
                    <div className="table-responsive">
                      <Table className="table-striped mb-0">
                        <thead>
                          <tr>
                            <th>#</th>
                            <th>Subcategory</th>
                            <th>Parent Category</th>
                            <th>Events</th>
                            <th>Trading Volume</th>
                            <th>Avg. Volume per Event</th>
                            <th>User Engagement</th>
                          </tr>
                        </thead>
                        <tbody>
                          {subcategoriesData.map((subcategory, index) => (
                            <tr key={subcategory.subcategoryId}>
                              <th scope="row">{index + 1}</th>
                              <td>{subcategory.subcategoryName}</td>
                              <td>{subcategory.categoryName}</td>
                              <td>{subcategory.eventCount}</td>
                              <td>${subcategory.tradingVolume.toFixed(2)}</td>
                              <td>
                                $
                                {(
                                  subcategory.tradingVolume /
                                  (subcategory.eventCount || 1)
                                ).toFixed(2)}
                              </td>
                              <td>{subcategory.userEngagement}</td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  </CardBody>
                </Card>
              </Col>
            </Row>
          </>
        )}
      </Container>
    </div>
  );
};

export default CategoryAnalytics;
